/**
 * Réception — liste des bons à réceptionner (PWA-first, août 2026).
 *
 * Accès réception simplifié : la tuile « Réception » de /accueil mène ici.
 * Un bon = une Delivery non « applied » (commande email, BL ou facture).
 * Tap sur un bon → parcours guidé /reception/:id (scan → rapprochement →
 * photo DLC-LOT → vérification → unité → approbation).
 *
 * « + Nouveau bon » colle l'email de confirmation fournisseur (parse IA
 * existant, POST /deliveries/parse-email-text). L'import fichier (BL/facture
 * PDF) et la mise en rayon restent sur la page complète /pos/reception.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { Delivery } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';

/** Le backend inclut les lignes réduites à {id, status} (non typées dans Delivery). */
type BonDelivery = Delivery & { lines?: { id: string; status: string }[] };

/** Un fichier déposé, suivi jusqu'à la création du bon. */
interface ImportJob {
  id: string;
  filename: string;
  jobId?: string;
  state: 'uploading' | 'queued' | 'parsing' | 'done' | 'error';
  deliveryId?: string;
  error?: string;
  /** Doublon détecté : le serveur renvoie de quoi proposer le remplacement. */
  duplicate?: { delivery_id: string; message: string };
}

const DOC_TYPE_LABELS: Record<string, string> = {
  ORDER: 'Commande',
  BL: 'Bon de livraison',
  INVOICE: 'Facture',
};

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function ReceptionBonsPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [deliveries, setDeliveries] = useState<BonDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sheet « Nouveau bon » : email collé (analyse immédiate) ou fichier importé
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState<'email' | 'file'>('email');
  const [emailText, setEmailText] = useState('');
  const [supplierHint, setSupplierHint] = useState('');
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  /**
   * Fichiers en cours d'analyse. Contrairement à l'email (réponse en ~3 s), un
   * PDF passe par une file d'attente serveur : on suit l'avancement ici plutôt
   * que de bloquer l'écran, l'opérateur pouvant continuer pendant ce temps.
   */
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!tenant?.id) return;
    setError(null);
    try {
      // Défaut backend : exclut les livraisons 'applied' → exactement « à réceptionner »
      const data = await api.listDeliveries(tenant.id, { limit: 50 });
      setDeliveries(data as BonDelivery[]);
    } catch {
      setError('Impossible de charger les bons. Vérifiez la connexion.');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    document.title = 'Réception — Folie Boutique';
    load();
  }, [load]);

  useEffect(() => {
    if (!importOpen || !tenant?.id || suppliers.length > 0) return;
    api.getSuppliers(tenant.id).then(setSuppliers).catch(() => {});
  }, [importOpen, tenant?.id, suppliers.length]);

  // ── Import de fichiers (BL, factures) ─────────────────────────────────────
  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || !tenant?.id) return;
    const hint = supplierHint.trim();
    for (const file of Array.from(files)) {
      const localId = `${file.name}-${file.size}-${jobs.length}-${file.lastModified}`;
      setJobs((prev) => [...prev, { id: localId, filename: file.name, state: 'uploading' }]);
      try {
        const res = await api.uploadAndParse(file, hint || undefined);
        setJobs((prev) => prev.map((j) =>
          j.id === localId ? { ...j, state: 'queued', jobId: res.job_id } : j));
      } catch (err: any) {
        const raw = err?.response?.data?.message || err?.message || 'Échec de l’envoi';
        // Le doublon remonte en JSON dans le message : le rendre lisible plutôt
        // que d'afficher un objet sérialisé à l'opérateur.
        let duplicate: ImportJob['duplicate'];
        let message = raw;
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.type === 'DUPLICATE_DELIVERY') {
            duplicate = { delivery_id: parsed.delivery_id, message: parsed.message };
            message = parsed.message;
          }
        } catch { /* message texte simple */ }
        setJobs((prev) => prev.map((j) =>
          j.id === localId ? { ...j, state: 'error', error: message, duplicate } : j));
      }
    }
    setImportOpen(false);
  };

  // Suivi des analyses en file d'attente
  useEffect(() => {
    const active = jobs.filter((j) => (j.state === 'queued' || j.state === 'parsing') && j.jobId);
    if (active.length === 0) return;
    let cancelled = false;
    const poll = async () => {
      for (const job of active) {
        try {
          const status = await api.getJobStatus(job.jobId!);
          if (cancelled || !status) continue;
          if (status.state === 'active') {
            setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, state: 'parsing' } : j)));
          } else if (status.state === 'completed' && status.result?.delivery_id) {
            setJobs((prev) => prev.map((j) =>
              j.id === job.id ? { ...j, state: 'done', deliveryId: status.result!.delivery_id } : j));
            load();
          } else if (status.state === 'failed') {
            setJobs((prev) => prev.map((j) =>
              j.id === job.id ? { ...j, state: 'error', error: 'Analyse impossible' } : j));
          }
        } catch { /* réseau : on retentera au tour suivant */ }
      }
    };
    const timer = setInterval(poll, 3000);
    poll();
    return () => { cancelled = true; clearInterval(timer); };
  }, [jobs, load]);

  const handleParseEmail = async () => {
    if (!tenant?.id || emailText.trim().length < 20) {
      setParseError('Collez le texte complet de l’email de confirmation (au moins quelques lignes).');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await api.parseEmailText(emailText, supplierHint.trim() || undefined);
      navigate(`/reception/${result.delivery_id}`);
    } catch (err: any) {
      setParseError(err?.response?.data?.message || 'Échec de l’analyse de l’email.');
    } finally {
      setParsing(false);
    }
  };

  const renderBon = (d: BonDelivery) => {
    const total = d.lines?.length ?? 0;
    const done = d.lines?.filter((l) => l.status !== 'pending').length ?? 0;
    const started = done > 0;
    return (
      <button
        key={d.id}
        onClick={() => navigate(`/reception/${d.id}`)}
        className="w-full text-left bg-white rounded-2xl border border-black/[.08] shadow-sm p-4
          active:scale-[.98] transition-transform duration-100 select-none touch-manipulation"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{d.supplier || 'Fournisseur inconnu'}</p>
            <p className="text-sm text-gray-500 truncate">
              {DOC_TYPE_LABELS[d.document_type] ?? d.document_type}
              {d.document_number ? ` · n° ${d.document_number}` : ''}
              {d.order_reference && !d.document_number ? ` · réf. ${d.order_reference}` : ''}
            </p>
          </div>
          <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full
            ${started ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'}`}>
            {started ? 'En cours' : 'À réceptionner'}
          </span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-[#2d6a4f] transition-all"
              style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
            />
          </div>
          <span className="text-sm text-gray-600 tabular-nums shrink-0">
            {done}/{total} lignes
          </span>
        </div>
        <p className="mt-2 text-xs text-gray-400 capitalize">{formatDate(d.document_date || d.created_at)}</p>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#faf7f0] pb-[max(2.5rem,env(safe-area-inset-bottom))]">
      <header className="px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-4 max-w-3xl mx-auto flex items-center gap-3">
        <Link
          to="/accueil"
          className="shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-white border border-black/[.08] shadow-sm
            active:scale-95 transition-transform select-none touch-manipulation"
          aria-label="Retour à l'accueil"
        >
          ←
        </Link>
        <div className="min-w-0">
          <p className="text-xs font-bold tracking-widest uppercase text-[#2d6a4f]">Réception</p>
          <h1 className="text-xl font-bold text-gray-900">Bons à réceptionner</h1>
        </div>
      </header>

      <main className="px-4 max-w-3xl mx-auto space-y-3">
        <button
          onClick={() => setImportOpen(true)}
          className="w-full h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold shadow-md
            active:scale-[.98] transition-transform select-none touch-manipulation"
        >
          + Nouveau bon (coller l’email)
        </button>

        {/* Fichiers en cours d'analyse — la file serveur met de quelques secondes
            à une minute ; sans ce suivi, l'import donnerait l'impression de ne
            rien faire. */}
        {jobs.map((j) => (
          <div
            key={j.id}
            className={`rounded-2xl border p-3 text-sm
              ${j.state === 'error' ? 'bg-red-50 border-red-200' : 'bg-white border-black/[.08]'}`}
          >
            <p className="font-semibold text-gray-900 truncate">📄 {j.filename}</p>
            {j.state === 'uploading' && <p className="text-gray-500">Envoi…</p>}
            {j.state === 'queued' && <p className="text-gray-500">En attente d’analyse…</p>}
            {j.state === 'parsing' && <p className="text-blue-600">Analyse en cours…</p>}
            {j.state === 'done' && j.deliveryId && (
              <button
                onClick={() => navigate(`/reception/${j.deliveryId}`)}
                className="mt-2 w-full h-11 rounded-xl bg-[#2d6a4f] text-white font-semibold
                  active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                Ouvrir le bon créé
              </button>
            )}
            {j.state === 'error' && (
              <>
                <p className="text-red-700">{j.error}</p>
                {j.duplicate && (
                  <button
                    onClick={() => navigate(`/reception/${j.duplicate!.delivery_id}`)}
                    className="mt-2 w-full h-11 rounded-xl bg-white border border-red-300 text-red-700 font-semibold
                      active:scale-[.98] transition-transform select-none touch-manipulation"
                  >
                    Ouvrir le bon existant
                  </button>
                )}
                <button
                  onClick={() => setJobs((prev) => prev.filter((x) => x.id !== j.id))}
                  className="mt-2 min-h-[48px] text-xs text-gray-500 underline underline-offset-2"
                >
                  Retirer
                </button>
              </>
            )}
          </div>
        ))}

        {loading && <p className="text-center text-gray-400 py-8">Chargement…</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm">
            {error}
            <button onClick={() => { setLoading(true); load(); }} className="ml-2 underline font-semibold">
              Réessayer
            </button>
          </div>
        )}
        {!loading && !error && deliveries.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-2" aria-hidden>📥</p>
            <p>Aucun bon en attente.</p>
            <p className="text-sm mt-1">Collez un email de confirmation pour en créer un.</p>
          </div>
        )}
        {deliveries.map(renderBon)}

        <footer className="pt-4 text-center space-y-2">
          {/* Liens à la taille du doigt : 48 px de haut (21/10/2026). */}
          <Link to="/pos/reception?tab=rayon" className="flex items-center justify-center min-h-[48px] text-base text-[#2d6a4f] font-semibold underline underline-offset-4">
            ⬆️ Mise en rayon
          </Link>
          <Link to="/pos/reception" className="flex items-center justify-center min-h-[48px] text-base text-gray-500 underline underline-offset-4">
            Version complète (onglets classiques)
          </Link>
        </footer>
      </main>

      {/* Sheet « Nouveau bon » */}
      {importOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Nouveau bon</h2>
              <button
                onClick={() => setImportOpen(false)}
                className="w-11 h-11 rounded-full bg-gray-100 active:scale-95 select-none touch-manipulation"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">Fournisseur (optionnel)</label>
            <input
              list="bons-suppliers"
              value={supplierHint}
              onChange={(e) => setSupplierHint(e.target.value)}
              placeholder="Ex. Interbio, Vajra…"
              className="w-full h-12 px-3 rounded-xl border border-gray-300 mb-3"
            />
            <datalist id="bons-suppliers">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
            {/* Deux origines : l'email de confirmation (prix clairs, analyse
                immédiate) et le fichier BL/facture, qui passe par la file
                d'analyse serveur. */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {([['email', '✉️ Coller un email'], ['file', '📄 Importer un fichier']] as const).map(
                ([mode, label]) => (
                  <button
                    key={mode}
                    onClick={() => { setImportMode(mode); setParseError(null); }}
                    className={`h-12 rounded-xl font-semibold border text-sm
                      active:scale-95 transition-transform select-none touch-manipulation
                      ${importMode === mode
                        ? 'bg-[#2d6a4f] text-white border-[#2d6a4f]'
                        : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {label}
                  </button>
                ),
              )}
            </div>

            {importMode === 'email' ? (
              <>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Email de confirmation de commande
                </label>
                <textarea
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                  placeholder="Collez ici le texte complet de l’email…"
                  rows={8}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm"
                />
                {parseError && <p className="text-sm text-red-600 mt-2">{parseError}</p>}
                <button
                  onClick={handleParseEmail}
                  disabled={parsing}
                  className="mt-4 w-full h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold shadow-md
                    disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
                >
                  {parsing ? 'Analyse en cours…' : 'Analyser et créer le bon'}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Bon de livraison ou facture (PDF, photo). L’analyse se fait en arrière-plan :
                  vous pouvez fermer cette fenêtre.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => { void handleFiles(e.target.files); e.target.value = ''; }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold shadow-md
                    active:scale-[.98] transition-transform select-none touch-manipulation"
                >
                  Choisir un ou plusieurs fichiers
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
